import type { ProcessResult } from '../types/staffImport';

export const STAFF_IMPORT_TEMPLATE_URL =
    '/staff-importer/staff-import-template.csv';

export interface StaffImportFieldRequirementRow {
    attributeName: string;
    requiredLabel: string;
    csvColumn: string;
}

export const STAFF_IMPORT_FIELD_REQUIREMENTS: StaffImportFieldRequirementRow[] =
    [
        {
            attributeName: 'First Name',
            requiredLabel: 'Yes',
            csvColumn: 'first_name',
        },
        {
            attributeName: 'Last Name',
            requiredLabel: 'Yes',
            csvColumn: 'last_name',
        },
        {
            attributeName: 'Staff Role',
            requiredLabel: 'Yes',
            csvColumn: 'role',
        },
        {
            attributeName: 'Email Address',
            requiredLabel: 'No (required only for login)',
            csvColumn: 'email',
        },
        {
            attributeName: 'Middle Name',
            requiredLabel: 'No',
            csvColumn: 'middle_name',
        },
        {
            attributeName: 'Employee ID',
            requiredLabel: 'No',
            csvColumn: 'employee_id',
        },
        {
            attributeName: 'Gender / Pronouns',
            requiredLabel: 'No',
            csvColumn: 'gender',
        },
        { attributeName: 'Street', requiredLabel: 'No', csvColumn: 'street' },
        { attributeName: 'City', requiredLabel: 'No', csvColumn: 'city' },
        { attributeName: 'State', requiredLabel: 'No', csvColumn: 'state' },
        {
            attributeName: 'Zip Code',
            requiredLabel: 'No',
            csvColumn: 'zipcode',
        },
        {
            attributeName: 'Country',
            requiredLabel: 'No',
            csvColumn: 'country',
        },
        {
            attributeName: 'Birthdate',
            requiredLabel: 'No',
            csvColumn: 'birthdate',
        },
        {
            attributeName: 'Primary Phone',
            requiredLabel: 'No',
            csvColumn: 'phone_number',
        },
        {
            attributeName: 'Online video conference link',
            requiredLabel: 'No',
            csvColumn: 'online_link',
        },
    ];

export const STAFF_IMPORT_MAX_CSV_BYTES = 10 * 1024 * 1024;

// Persisted / sent to the backend as `fileName` — do not change this text.
export const WEBSITE_IMPORT_LABEL = 'Website import';

export const STAFF_IMPORT_PAGE_COPY = {
    title: 'Staff Importer',
    businessContext: (businessName: string) =>
        `You are about to import staff for ${businessName}`,
    intro: 'Import basic staff fields from an uploaded CSV or scan a website url to generate your staff.',
    fieldRequirementsLabel: 'View field requirements',
    fieldRequirementsModalTitle: 'CSV Requirements',
    fieldRequirementsColumnAttribute: 'Attribute Name',
    fieldRequirementsColumnRequired: 'Value Required',
    fieldRequirementsColumnCsv: 'CSV Column Name',
    downloadTemplateLabel: 'Download our Staff Importer Template',
    webCrawlLabel: 'Scan a website for staff',
    webCrawlPlaceholder: 'https://your-studio.com/our-team',
    webCrawlButtonLabel: 'Scan URL for Staff',
    webCrawlHelperText:
        'For best results, provide the exact URL where your staff are listed. Ex https://yourbusiness.com/staff',
    csvUploadLabel: 'Upload your CSV',
    csvUploadPlaceholder: 'Click here to upload a CSV file',
    uploadFileLabel: 'Upload File',
    replaceFileWarningTitle: 'Abandon current import?',
    replaceFileWarningMessage:
        'You still have an import in progress. The contents of the grid editor will be replaced/lost. Are you sure you want to continue?',
    replaceFileWarningUploadLabel: 'Upload New File',
    replaceWebCrawlWarningConfirmLabel: 'Load new website',
    uploadedSuccessMessage: (fileName: string) =>
        `${fileName} was successfully uploaded.`,
    lastSuccessfulJobMessage: (formattedTimestamp: string) =>
        `Last successful extraction: ${formattedTimestamp}`,
    pollingMessage: 'Processing your file…',
    scanningTitle: 'Scanning…',
    scanningMessage:
        "This won't take long. We're exploring your website looking for staff.",
    websiteImportSuccessMessage: (sourceLabel: string) =>
        sourceLabel === WEBSITE_IMPORT_LABEL
            ? 'Staff details were loaded from your website.'
            : `Staff details were loaded from ${sourceLabel}.`,
    websiteImportPollingMessage: 'Loading staff from your website…',
    processImportLabel: 'Process Import',
    processImportDisabledAwaitImport:
        'Upload a CSV or scan a website to begin.',
    processImportDisabledAnalyzing: 'Wait for import analysis to complete.',
    processImportDisabledProcessing: 'Import is in progress.',
    processImportDisabledValidation:
        'Fix validation errors before processing the import.',
    confirmImportLabel: 'Confirm Import',
    cancelLabel: 'Cancel',
    confirmModalTitle: (count: number) =>
        `Import ${count} Staff Account${count === 1 ? '' : 's'}?`,
    confirmModalIntro: (count: number) =>
        `You're about to import ${count} staff account${count === 1 ? '' : 's'}.`,
    confirmModalUndoNote:
        "This can't be undone, but you can edit or deactivate accounts afterward.",
    confirmModalConfirmPrompt: 'Click "Confirm Import" to continue.',
    abandonModalTitle: 'Unsaved changes',
    abandonModalMessage:
        'You have unsaved changes. Are you sure you want to leave?',
    abandonModalCancelLabel: 'Cancel',
    abandonModalLeavePageLabel: 'Leave Page',
    abandonModalDiscardImportTitle: 'Abandon current import?',
    abandonModalDiscardImportMessage:
        "You still have an import in progress. If you cancel, your uploaded file and grid will be cleared and you'll need to start over.",
    abandonModalDiscardImportLabel: 'Discard Import',
    completeTitle: 'Import complete',
    completeMessage: (count: number, fileName: string) =>
        `Successfully imported ${count} staff member${count === 1 ? '' : 's'} from ${fileName}.`,
    completeFailedTitle: 'Import failed',
    completeFailedMessage: (fileName: string, failedCount: number) =>
        `No staff accounts were imported from ${fileName}. ${failedCount} row${failedCount === 1 ? '' : 's'} could not be imported. Review the errors below and try again.`,
    completePartialMessage: (
        importedCount: number,
        failedCount: number,
        fileName: string,
    ) =>
        `Successfully imported ${importedCount} staff member${importedCount === 1 ? '' : 's'} from ${fileName}. ${failedCount} row${failedCount === 1 ? '' : 's'} failed.`,
    importAnotherFileLabel: 'Import another file',
    validationBannerTitle: (errorCount: number) =>
        `${errorCount} validation error${errorCount === 1 ? '' : 's'} found`,
    validationBannerSubtitle:
        'Fix the errors below before processing the import.',
    readyForImportMessage: 'File ready for import. No errors or issues found.',
    gridValidationIntroMessage:
        'The following errors were detected in your CSV. Please update the following items to ensure that staff are successfully imported:',
    emailColumnSubheader: 'Required for login',
    gridLegendBlockingLabel: 'Missing required field or validation error',
    gridLegendOptionalLabel: 'Missing optional field (required for login)',
    gridReadyWithWarningsMessage: (warningCount: number) =>
        `File ready for import. ${warningCount} optional field warning(s) remain.`,
    gridValidationIssueCountMessage: (errorCount: number) =>
        `${errorCount} validation issue(s) detected.`,
    gridDisabledMessage: 'Upload a CSV file to preview and edit staff rows.',
    addRowLabel: 'Add New Row to Import',
};

export const validateCsvFile = (
    file: File,
): { valid: true } | { valid: false; error: string } => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
        return { valid: false, error: 'File must be a .csv file.' };
    }
    if (file.size === 0) {
        return { valid: false, error: 'File cannot be empty.' };
    }
    if (file.size > STAFF_IMPORT_MAX_CSV_BYTES) {
        return {
            valid: false,
            error: 'File exceeds the 10 MB size limit.',
        };
    }
    return { valid: true };
};

export type StaffImportCompleteSummary = {
    title: string;
    message: string;
    variant: 'success' | 'warning' | 'error';
};

export const deriveStaffImportCompleteSummary = (
    result: ProcessResult,
): StaffImportCompleteSummary => {
    if (result.importedCount === 0 && result.failedCount > 0) {
        return {
            title: STAFF_IMPORT_PAGE_COPY.completeFailedTitle,
            message: STAFF_IMPORT_PAGE_COPY.completeFailedMessage(
                result.fileName,
                result.failedCount,
            ),
            variant: 'error',
        };
    }

    if (result.failedCount > 0) {
        return {
            title: STAFF_IMPORT_PAGE_COPY.completeTitle,
            message: STAFF_IMPORT_PAGE_COPY.completePartialMessage(
                result.importedCount,
                result.failedCount,
                result.fileName,
            ),
            variant: 'warning',
        };
    }

    return {
        title: STAFF_IMPORT_PAGE_COPY.completeTitle,
        message: STAFF_IMPORT_PAGE_COPY.completeMessage(
            result.importedCount,
            result.fileName,
        ),
        variant: 'success',
    };
};
